import React from 'react';
import { Check, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Flat, Minimal Template Card Component
 * Colors: #D45614 (primary) and white only
 */
const TemplateCard = ({ template, isSelected, onSelect, onPreview, isPopular = false, viewMode = 'grid' }) => {
    const navigate = useNavigate();

    const handleViewTemplate = (e) => {
        e.stopPropagation();
        if (onPreview) {
            onPreview(template);
        } else {
            navigate(`/template${template.id}`);
        }
    };

    const handleSelectTemplate = (e) => {
        e.stopPropagation();
        onSelect(template);
    };

    // List View - Flat design
    if (viewMode === 'list') {
        return (
            <div
                onClick={handleSelectTemplate}
                className={`group flex items-center gap-6 py-4 cursor-pointer transition-all duration-200 border-b border-gray-100 ${
                    isSelected ? 'bg-[#D45614]/5' : 'hover:bg-gray-50'
                }`}
            >
                {/* Thumbnail */}
                <div className={`relative w-32 h-20 flex-shrink-0 overflow-hidden ${isSelected ? 'ring-2 ring-[#D45614]' : ''}`}>
                    <img
                        src={template.image}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.src = `https://via.placeholder.com/320x224/f3f4f6/9ca3af?text=${encodeURIComponent(template.name)}`;
                        }}
                    />
                    {isSelected && (
                        <div className="absolute top-1 right-1 bg-[#D45614] text-white p-1 rounded-full">
                            <Check className="w-3 h-3" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className={`text-base font-medium ${isSelected ? 'text-[#D45614]' : 'text-gray-900'}`}>
                            {template.name}
                        </h3>
                        {isSelected && (
                            <span className="text-xs font-medium text-[#D45614]">Selected</span>
                        )}
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1">{template.description}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                        onClick={handleViewTemplate}
                        className="text-sm text-gray-500 hover:text-[#D45614] transition-colors"
                    >
                        Preview
                    </button>
                    <button
                        onClick={handleSelectTemplate}
                        className={`text-sm font-medium px-4 py-1.5 transition-colors ${
                            isSelected
                                ? 'bg-[#D45614] text-white'
                                : 'text-[#D45614] hover:bg-[#D45614] hover:text-white'
                        }`}
                    >
                        {isSelected ? 'Selected' : 'Select'}
                    </button>
                </div>
            </div>
        );
    }

    // Grid View - Flat design without cards
    return (
        <div
            onClick={handleSelectTemplate}
            className={`group cursor-pointer transition-all duration-200 ${
                isSelected ? 'transform scale-[1.02]' : ''
            }`}
        >
            {/* Image Container - No card, just image with selection indicator */}
            <div className={`relative aspect-[4/3] overflow-hidden mb-3 ${
                isSelected ? 'ring-3 ring-[#D45614]' : ''
            }`}>
                <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                        e.target.src = `https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=${encodeURIComponent(template.name)}`;
                    }}
                />

                {/* Selected indicator */}
                {isSelected && (
                    <div className="absolute top-3 right-3 bg-[#D45614] text-white p-2 rounded-full">
                        <Check className="w-4 h-4" />
                    </div>
                )}

                {/* Hover overlay - Simple */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleViewTemplate}
                            className="bg-white text-gray-700 px-4 py-2 text-sm font-medium transform translate-y-2 group-hover:translate-y-0 transition-all duration-200 hover:bg-gray-100 flex items-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            Preview
                        </button>
                        <button
                            onClick={handleSelectTemplate}
                            className="bg-[#D45614] text-white px-4 py-2 text-sm font-medium transform translate-y-2 group-hover:translate-y-0 transition-all duration-200 delay-75 hover:bg-[#c04d12]"
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>

            {/* Info - Flat, no card background */}
            <div>
                <h3 className={`text-base font-medium mb-1 ${isSelected ? 'text-[#D45614]' : 'text-gray-900'}`}>
                    {template.name}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2">{template.description}</p>
            </div>
        </div>
    );
};

export default TemplateCard;
